import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  useReactFlow,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  ArrowLeft,
  Save,
  Play,
  Wand2,
  RotateCcw,
  CheckCircle,
  ShieldCheck,
  History,
  AlertCircle,
  Clock,
  RefreshCw,
  Map,
  Minimize2,
  LayoutTemplate,
  BookmarkPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';

import api from '../services/api';
import { useWorkflowStore } from '../hooks/useWorkflowStore';
import { nodeTypes } from '../components/flow/CustomNodes';
import { edgeTypes } from '../components/flow/CustomEdges';
import BlockPaletteSidebar from '../components/flow/BlockPaletteSidebar';
import NodeConfigDrawer from '../components/flow/NodeConfigDrawer';
import FlowTestSimulator from '../components/flow/FlowTestSimulator';
import LoadTemplateModal from '../components/flow/LoadTemplateModal';
import SaveAsTemplateModal from '../components/flow/SaveAsTemplateModal';

function FlowCanvas({ campaignId }) {
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const onNodesChange = useWorkflowStore((state) => state.onNodesChange);
  const onEdgesChange = useWorkflowStore((state) => state.onEdgesChange);
  const onConnect = useWorkflowStore((state) => state.onConnect);
  const addNode = useWorkflowStore((state) => state.addNode);
  const setSelectedNodeId = useWorkflowStore((state) => state.setSelectedNodeId);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const rawData = event.dataTransfer.getData('application/reactflow');

      if (!rawData) return;

      const template = JSON.parse(rawData);
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(
        {
          type: template.type,
          data: {
            label: template.title || template.label || template.type,
            subtitle: template.subtitle,
            icon: template.icon,
            ...template,
          },
        },
        position
      );

      toast.success(`Added ${template.title || template.type} block`);
    },
    [screenToFlowPosition, addNode]
  );

  const { setCenter } = useReactFlow();
  const [miniMapCollapsed, setMiniMapCollapsed] = useState(false);

  return (
    <div
      className="reactflow-wrapper"
      ref={reactFlowWrapper}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onPaneClick={() => setSelectedNodeId(null)}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        defaultEdgeOptions={{ type: 'custom', animated: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1.5}
          color="var(--text-muted)"
          style={{ opacity: 0.3 }}
        />
        <Controls position="bottom-left" style={{ margin: 16 }} />

        {/* MiniMap with Panel Toggle Button */}
        <Panel
          position="bottom-right"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 8,
            margin: '0 16px 48px 0',
          }}
        >
          <button
            onClick={() => setMiniMapCollapsed(!miniMapCollapsed)}
            style={{
              padding: '5px 12px',
              borderRadius: 20,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-primary)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: 'var(--shadow)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            title={miniMapCollapsed ? 'Expand MiniMap' : 'Collapse MiniMap'}
          >
            <Map size={13} style={{ color: 'var(--accent)' }} />
            {miniMapCollapsed ? 'Show MiniMap' : 'Hide MiniMap'}
          </button>

          {!miniMapCollapsed && (
            <MiniMap
              zoomable
              pannable
              onNodeClick={(event, node) => {
                setSelectedNodeId(node.id);
                setCenter(node.position.x + 135, node.position.y + 60, { zoom: 1.2, duration: 600 });
              }}
              nodeColor={(n) => {
                if (n.type === 'start') return 'var(--amber)';
                if (n.type === 'condition') return 'var(--purple)';
                if (n.type === 'route') return 'var(--blue)';
                return 'var(--text-muted)';
              }}
              style={{
                margin: 0,
                borderRadius: 12,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            />
          )}
        </Panel>

        {/* Floating status legend */}
        <Panel position="top-right">
          <div
            style={{
              padding: '6px 12px',
              borderRadius: 20,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--green)',
            }}
          >
            <ShieldCheck size={14} /> Backend Schema Sync Active
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
}

export default function CampaignFlowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [campaignVertical, setCampaignVertical] = useState(null);

  const loadDraft = useWorkflowStore((state) => state.loadDraft);
  const saveDraft = useWorkflowStore((state) => state.saveDraft);
  const publishFlow = useWorkflowStore((state) => state.publishFlow);
  const loadVersions = useWorkflowStore((state) => state.loadVersions);
  const rollbackVersion = useWorkflowStore((state) => state.rollbackVersion);

  const autoLayout = useWorkflowStore((state) => state.autoLayout);
  const resetFlow = useWorkflowStore((state) => state.resetFlow);

  const versions = useWorkflowStore((state) => state.versions);
  const lastSavedAt = useWorkflowStore((state) => state.lastSavedAt);
  const loading = useWorkflowStore((state) => state.loading);

  useEffect(() => {
    if (id) {
      loadDraft(id);
      loadVersions(id);
      // Only used to default/filter the template picker to this campaign's
      // vertical - not fetched anywhere else in this app yet, so degrade
      // quietly (picker just falls back to showing all verticals) if it 404s.
      api.get(`/campaigns/${id}`)
        .then((res) => setCampaignVertical(res.data?.vertical || null))
        .catch(() => setCampaignVertical(null));
    }
  }, [id, loadDraft, loadVersions]);

  const handleSave = async () => {
    setSaving(true);
    setValidationErrors([]);
    try {
      await saveDraft(id);
      toast.success('Draft flow saved to backend!');
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Draft conflict: another session updated this flow. Reloading...');
        loadDraft(id);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setValidationErrors([]);
    try {
      const res = await publishFlow(id);
      toast.success(`Published routing flow version #${res.version?.version || 'snapshot'}!`);
      loadVersions(id);
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
        toast.error('Publish failed: graph validation errors found');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to publish flow');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async (versionId) => {
    try {
      await rollbackVersion(id, versionId);
      toast.success('Rolled back to selected version snapshot!');
      setShowHistory(false);
    } catch (err) {
      toast.error('Rollback failed');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg-base)',
      }}
    >
      {/* Top Header Navigation */}
      <header
        className="topbar"
        style={{
          height: 60,
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => navigate('/campaigns')}
            title="Back to Campaigns"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                Campaign Routing Flow Editor
              </h2>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent)',
                  padding: '2px 8px',
                  borderRadius: 99,
                  textTransform: 'uppercase',
                }}
              >
                Draft
              </span>
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Campaign ID: #{id} {lastSavedAt ? `· Last saved: ${new Date(lastSavedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
        </div>

        {/* Toolbar Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={autoLayout}
            title="Auto Align & Organize Blocks"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-strong)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <Wand2 size={14} style={{ color: 'var(--blue)' }} /> Auto Layout
          </button>

          <button
            onClick={() => setShowSimulator(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              background: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 8,
              color: '#FBBF24',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <Play size={14} style={{ color: '#FBBF24' }} /> Test Simulation
          </button>

          <button
            onClick={() => setShowHistory(true)}
            title="Version History & Rollback"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              background: 'rgba(168, 85, 247, 0.12)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: 8,
              color: '#C084FC',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <History size={14} style={{ color: '#C084FC' }} /> Version History
          </button>

          <button
            onClick={resetFlow}
            title="Reset to Default Graph"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              padding: 0,
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-strong)',
              borderRadius: 8,
              color: 'var(--text-muted)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            className="toolbar-btn"
          >
            <RotateCcw size={14} />
          </button>

          <button
            onClick={() => setShowLoadTemplate(true)}
            title="Load from Template"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-strong)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <LayoutTemplate size={14} style={{ color: 'var(--accent)' }} /> Load from Template
          </button>

          <button
            onClick={() => setShowSaveAsTemplate(true)}
            title="Save as Template"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 13px',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-strong)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <BookmarkPlus size={14} style={{ color: 'var(--accent)' }} /> Save as Template
          </button>

          <div style={{ width: 1, height: 22, background: 'var(--border)', margin: '0 4px' }} />

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              background: 'var(--bg-raised)',
              border: '1px solid var(--border-strong)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 12,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'var(--shadow-sm)',
            }}
            className="toolbar-btn"
          >
            <Save size={14} /> {saving ? 'Saving...' : 'Save Draft'}
          </button>

          <button
            onClick={handlePublish}
            disabled={publishing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 16px',
              background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
              border: 'none',
              borderRadius: 8,
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 700,
              cursor: publishing ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
            }}
            className="toolbar-btn-primary"
          >
            <CheckCircle size={14} /> {publishing ? 'Publishing...' : 'Publish Flow'}
          </button>
        </div>
      </header>

      {/* Validation Errors Notice Banner */}
      {validationErrors.length > 0 && (
        <div
          style={{
            background: 'var(--red-bg)',
            borderBottom: '1px solid var(--red)',
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--red)',
            fontSize: 12,
            zIndex: 15,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={16} />
            <div>
              <strong>Validation Errors Gating Publish:</strong> {validationErrors.join(' | ')}
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--red)' }}
            onClick={() => setValidationErrors([])}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Main Canvas Workspace with Sidebars */}
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        <BlockPaletteSidebar />

        <div style={{ flex: 1, height: '100%', position: 'relative' }}>
          {loading ? (
            <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <RefreshCw size={20} className="spin" style={{ color: 'var(--accent)' }} /> Loading backend flow draft...
            </div>
          ) : (
            <ReactFlowProvider>
              <FlowCanvas campaignId={id} />
            </ReactFlowProvider>
          )}
        </div>

        <NodeConfigDrawer campaignId={id} />
      </div>

      {/* Test Simulator Modal */}
      {showSimulator && (
        <FlowTestSimulator onClose={() => setShowSimulator(false)} />
      )}

      {/* Load from Template Modal */}
      {showLoadTemplate && (
        <LoadTemplateModal
          campaignId={id}
          campaignVertical={campaignVertical}
          onClose={() => setShowLoadTemplate(false)}
        />
      )}

      {/* Save as Template Modal */}
      {showSaveAsTemplate && (
        <SaveAsTemplateModal
          campaignId={id}
          campaignVertical={campaignVertical}
          onClose={() => setShowSaveAsTemplate(false)}
        />
      )}

      {/* Version History Modal */}
      {showHistory && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: 540,
              background: 'var(--bg-surface)',
              borderRadius: 16,
              boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={18} /> Published Versions & Rollback
              </h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowHistory(false)}>✕</button>
            </div>
            <div style={{ padding: 16, maxHeight: 350, overflowY: 'auto' }}>
              {versions.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                  No published versions snapshot found for this campaign yet.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        background: 'var(--bg-raised)',
                        border: '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Version #{v.version}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          Published on {new Date(v.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleRollback(v.id)}
                      >
                        Rollback to this
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
